import { ElementRef, Injectable } from '@angular/core';
import { PosHttpService } from '../../services/pos.http.service';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5plugins_exporting from '@amcharts/amcharts5/plugins/exporting';
import { FnAppSessionService } from 'foundation';

@Injectable({
  providedIn: 'root'
})
export class ContextEventService {
  elementRef: any;
  chartData: { [key: string]: ChartDataPoint[] } = {};
  private hostChartRoot: am5.Root | null = null;
  private modal: HTMLElement | null = null;

  constructor(private posHttpSvcs: PosHttpService, private apSessionSvcs: FnAppSessionService) { }

  setChartData(data: any): void {
    this.chartData = data;
  }

  createCharts(elementRef: ElementRef, selectedHostEvent: string): void {
    const chartContainer = elementRef.nativeElement.querySelector('#context_event');
    if (!chartContainer) {
      console.error('Chart container not found');
      return;
    }

    chartContainer.innerHTML = '';

    this.createModal();

    Object.entries(this.chartData).forEach(([schemaName, data]: [string, ChartDataPoint[]]) => {
      const chartDiv = document.createElement('div');
      chartDiv.style.width = '100%';
      chartDiv.style.height = '400px';
      chartContainer.appendChild(chartDiv);

      const root = am5.Root.new(chartDiv);
      if (root._logo) {
        root._logo.dispose();
      }

      root.container.set("background", am5.Rectangle.new(root, {
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x121418 : 0xf3f1f1),
        fillOpacity: 1
      }));

      root.setThemes([am5themes_Animated.new(root)]);

      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: true,
          panY: true,
          wheelX: 'panX',
          wheelY: 'zoomX',
          layout: root.verticalLayout,
          paddingLeft: 10,
          paddingRight: 20,
        })
      );

      chart.children.unshift(
        am5.Label.new(root, {
          text: `${schemaName} (${selectedHostEvent})`,
          fontSize: 18,
          fill: am5.color(0xFF3333),
          textAlign: 'center',
          x: am5.percent(50),
          centerX: am5.percent(50),
          fontWeight: 'bold',
        })
      );
      const countMax = Math.max(...data.map((d) => d.count));
      const countMin = Math.min(...data.map((d) => d.count));

      am5plugins_exporting.Exporting.new(root, {
        menu: am5plugins_exporting.ExportingMenu.new(root, {}),
        dataSource: this.chartData
      });

      chart.children.push(
        am5.Label.new(root, {
          text: `[#87CEFA]■[/] Count: Max: ${countMax},  Min: ${countMin}`,
          fontSize: 14,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xF3F3F3 : 0x000000),
          fontWeight: 'normal',
          textAlign: 'left',
          x: am5.percent(0),
          centerX: am5.percent(0),
          paddingLeft: 10,
          paddingTop: 10,
          paddingBottom: 10,
        })
      );

      const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
      xRenderer.grid.template.setAll({
        strokeOpacity: 0,
      });
      xRenderer.setAll({
        stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xF3F3F3 : 0x000000),
        strokeWidth: 1,
        strokeOpacity: 1,
      });
      xRenderer.labels.template.setAll({
        rotation: -90,
        centerY: am5.percent(50),
        centerX: am5.percent(50),
        paddingTop: 10,
        fontSize: 12,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        visible: true,
        paddingRight: 10,
      });
      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          renderer: xRenderer,
          categoryField: 'time',
          tooltip: am5.Tooltip.new(root, {}),
        })
      );
      xAxis.children.push(
        am5.Label.new(root, {
          text: 'Time',
          fontSize: 14,
          fontWeight: 'bold',
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xF3F3F3 : 0x000000),
          x: am5.percent(50),
          centerX: am5.percent(50),
        })
      );
      xAxis.data.setAll(data);

      const yRenderer = am5xy.AxisRendererY.new(root, {});
      yRenderer.grid.template.setAll({
        strokeOpacity: 0,
      });
      yRenderer.setAll({
        stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xF3F3F3 : 0x000000),
        strokeWidth: 1,
        strokeOpacity: 1,
      });
      yRenderer.labels.template.setAll({
        fontSize: 12,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        visible: true,
        paddingRight: 10,
      });
      const yAxisLeft = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: yRenderer,
          min: 0,
          extraMax: 0.1,
          numberFormat: '#',
        })
      );
      yAxisLeft.children.unshift(
        am5.Label.new(root, {
          text: 'Count',
          fontSize: 14,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xF3F3F3 : 0x000000),
          rotation: -90,
          x: -2,
          y: am5.percent(50),
          centerY: am5.percent(50),
          centerX: am5.percent(50),
        })
      );

      const barSeries = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: 'Count',
          xAxis: xAxis,
          yAxis: yAxisLeft,
          valueYField: 'count',
          categoryXField: 'time',
          tooltip: am5.Tooltip.new(root, {
            labelText: 'Count: {valueY}\nClick to view host details',
          }),
        })
      );

      barSeries.data.setAll(data);
      barSeries.columns.template.setAll({
        maxWidth: 50,
        width: am5.percent(80),
        fill: am5.color(0x87CEFA),
        strokeOpacity: 0,
      });

      barSeries.bullets.push(() =>
        am5.Bullet.new(root, {
          locationY: 1,
          sprite: am5.Label.new(root, {
            text: '{valueY}',
            fontSize: 12,
            fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
            centerX: am5.percent(50),
            centerY: am5.percent(50),
            dy: -10,
            populateText: true,
          }),
        })
      );

      barSeries.columns.template.adapters.add('fill', (fill, target) => {
        const dataItem = target.dataItem as am5.DataItem<am5xy.IColumnSeriesDataItem> | undefined;
        if (dataItem) {
          const count = dataItem.get('valueY');
          if (typeof count === 'number' && count >= 10) {
            return am5.color(this.apSessionSvcs.isDarkTheme() ? 0xDFBB52 : 0x87CEFA);
          }
        }
        return am5.color(0x87CEFA);
      });
      barSeries.columns.template.events.on('click', (ev) => {
        const dataItem = ev.target.dataItem as am5.DataItem<am5xy.IColumnSeriesDataItem>;
        const time = dataItem.get('categoryX');
        if (!time) return;

        const schemaData = this.chartData[schemaName].find((d) => d.time === time);
        if (!schemaData || !schemaData.serverMapList) return;

        if (this.hostChartRoot) {
          this.hostChartRoot.dispose();
          this.hostChartRoot = null;
        }

        const modalContent = this.modal?.querySelector('#modal-chart');
        if (modalContent && this.modal) {
          this.hostChartRoot = this.createHostWiseChart(modalContent as HTMLElement, schemaName, time, schemaData.serverMapList);
          this.modal.style.display = 'block';
        }
      });
    });
  }

  private createModal(): void {
    if (this.modal) return;

    this.modal = document.createElement('div');
    this.modal.id = 'host-chart-modal';
    this.modal.style.display = 'none';
    this.modal.style.position = 'fixed';
    this.modal.style.top = '0';
    this.modal.style.left = '0';
    this.modal.style.width = '100%';
    this.modal.style.height = '100%';
    this.modal.style.backgroundColor = 'rgba(0,0,0,0.5)';
    this.modal.style.zIndex = '1000';
    this.modal.innerHTML = `
      <div id="modal-content" style="background: white; margin: 50px auto; padding: 20px; width: 1200px; max-height: 80%; overflow: auto; border: 2px solid #333; border-radius: 5px;">
        <div id="modal-chart" style="width: 100%; height: 400px;"></div>
      </div>
    `;

    document.body.appendChild(this.modal);

    this.modal.addEventListener('click', (event) => {
      const modalContent = this.modal?.querySelector('#modal-content');
      if (modalContent && !modalContent.contains(event.target as Node)) {
        console.log('Clicked outside modal');
        if (this.modal) {
          this.modal.style.display = 'none';
        }
        if (this.hostChartRoot) {
          this.hostChartRoot.dispose();
          this.hostChartRoot = null;
        }
      }
    });
  }

  createHostWiseChart(
    container: HTMLElement,
    schemaName: string,
    time: string,
    serverMapList: any[]
  ): am5.Root {
    container.innerHTML = '';

    const hostData = serverMapList.map((server) => {
      const serverName = server.serverName;
      const totalCount = server.count;
      const contextDetail = server.contextDetail;

      console.log(`Raw contextDetail for server ${serverName}:`, contextDetail);

      let contextBreakdown: { context: string; count: number }[] = [];
      if (typeof contextDetail === 'string' && contextDetail.trim() !== '') {
        const rawBreakdown: ({ context: string; count: number } | null)[] = contextDetail
          .split('\n')
          .map((line: string) => {
            const match = line.match(/(.+?) - (\d+)/);
            return match ? { context: match[1].trim(), count: parseInt(match[2], 10) } : null;
          });
        contextBreakdown = rawBreakdown.filter(
          (d): d is { context: string; count: number } => d !== null && d.count > 0
        );
      }

      console.log(`Parsed contextBreakdown for server ${serverName}:`, contextBreakdown);

      const breakdownText = contextBreakdown.length > 0
        ? contextBreakdown.map((ctx) => `${ctx.context}: ${ctx.count}`).join('\n')
        : 'No host details available';
      const tooltipText = `Server: ${serverName}\nTotal Count: ${totalCount}\n${breakdownText}`;

      return {
        server: serverName,
        count: totalCount,
        contextBreakdown: contextBreakdown,
        tooltipText: tooltipText,
      };
    });

    const root = am5.Root.new(container);
    if (root._logo) {
      root._logo.dispose();
    }

    root.container.set("background", am5.Rectangle.new(root, {
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xf1e7c4 : 0xf3f1f1),
      fillOpacity: 1
    }));

    root.setThemes([am5themes_Animated.new(root)]);
    am5plugins_exporting.Exporting.new(root, {
      menu: am5plugins_exporting.ExportingMenu.new(root, {}),
      dataSource: hostData
    });

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        layout: root.verticalLayout,
      })
    );

    chart.children.unshift(
      am5.Label.new(root, {
        text: `${schemaName} Host-Wise Count at ${time}`,
        fontSize: 18,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xf50000 : 0x000000),
        textAlign: 'center',
        x: am5.percent(50),
        centerX: am5.percent(50),
        fontWeight: 'bold',
      })
    );

    const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
    xRenderer.grid.template.setAll({
      strokeOpacity: 0,
    });
    xRenderer.setAll({
      stroke: am5.color(0x000000),
      strokeWidth: 1,
      strokeOpacity: 1,
    });
    xRenderer.labels.template.setAll({
      rotation: -90,
      centerY: am5.percent(50),
      centerX: am5.percent(50),
      paddingTop: 10,
    });
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        renderer: xRenderer,
        categoryField: 'server',
        tooltip: am5.Tooltip.new(root, {}),
      })
    );

    xAxis.children.push(
      am5.Label.new(root, {
        text: 'Server',
        fontSize: 14,
        fontWeight: 'bold',
        fill: am5.color(0x000000),
        x: am5.percent(50),
        centerX: am5.percent(50),
      })
    );
    xAxis.data.setAll(hostData);

    const yRenderer = am5xy.AxisRendererY.new(root, {});
    yRenderer.grid.template.setAll({
      strokeOpacity: 0,
    });
    yRenderer.setAll({
      stroke: am5.color(0x000000),
      strokeWidth: 1,
      strokeOpacity: 1,
    });
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: yRenderer,
        min: 0,
        extraMax: 0.1,
        numberFormat: '#',
      })
    );

    yAxis.children.unshift(
      am5.Label.new(root, {
        text: 'Count',
        fontSize: 14,
        fontWeight: 'bold',
        fill: am5.color(0x000000),
        rotation: -90,
        x: -2,
        y: am5.percent(50),
        centerY: am5.percent(50),
        centerX: am5.percent(50),
      })
    );

    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Server Count',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'count',
        categoryXField: 'server',
      })
    );

    const tooltip = am5.Tooltip.new(root, {
      pointerOrientation: 'horizontal',
    });
    series.set("tooltip", tooltip);

    tooltip.get("background")?.setAll({
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x121418 : 0xf3f1f1),
      fillOpacity: 1,
      stroke: am5.color(0x000000),
      strokeWidth: 1,
    });

    series.columns.template.adapters.add("tooltipText", (text, target) => {
      const dataItem = target.dataItem as am5.DataItem<am5xy.IColumnSeriesDataItem>;
      if (dataItem) {
        const dataContext = dataItem.dataContext as any;
        if (dataContext) {
          const serverName = dataContext.server;
          const totalCount = dataContext.count;
          const contextBreakdown = dataContext.contextBreakdown || [];
          const breakdownText = contextBreakdown.length > 0
            ? contextBreakdown.map((ctx: any) => `${ctx.context}: ${ctx.count}`).join('\n')
            : 'No host details available';
          return `Server: ${serverName}\nTotal Count: ${totalCount}\n${breakdownText}`;
        }
      }
      return 'No data available';
    });

    const countMax = Math.max(...hostData.map((d) => d.count));
    const countMin = Math.min(...hostData.map((d) => d.count));

    chart.children.push(
      am5.Label.new(root, {
        text: `[#87CEFA]■[/] Count: Max: ${countMax},  Min: ${countMin}`,
        fontSize: 14,
        fill: am5.color(0x000000),
        fontWeight: 'normal',
        textAlign: 'left',
        x: am5.percent(0),
        centerX: am5.percent(0),
        paddingLeft: 10,
        paddingTop: 10,
        paddingBottom: 10,
      })
    );

    series.data.setAll(hostData);

    series.columns.template.setAll({
      maxWidth: 50,
      width: am5.percent(80),
      fill: am5.color(0x87CEFA),
      strokeOpacity: 0,
    });

    series.bullets.push(() =>
      am5.Bullet.new(root, {
        locationY: 1,
        sprite: am5.Label.new(root, {
          text: '{valueY}',
          fontSize: 12,
          fill: am5.color(0x000000),
          centerX: am5.percent(50),
          centerY: am5.percent(50),
          dy: -10,
          populateText: true,
        }),
      })
    );

    return root;
  }
}

interface ChartDataPoint {
  schema?: string;
  avg: number;
  busiDate?: string;
  count: number;
  index?: number;
  time: string;
  id?: string;
  hostWiseCount?: string;
  serverMapList?: any[];
  color?: string;
}
